import React, { lazy, useState, useEffect } from 'react';
import { Route, Navigate, Outlet } from 'react-router-dom';
import routes from '../config/routes';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import PatientHeader from '../components/PatientHeader';
import MobilePatientHeader from '../components/MobilePatientHeader';
import PatientBottomNavigation from '../components/PatientBottomNavigation';
import { checkPatientAuth } from '../utils/patientAuth';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { MessageUnreadProvider, useMessageUnread } from '../context/MessageUnreadContext';

// Lazy load patient portal pages
const PatientLoginPage = lazy(() => import('../pages/patient/PatientLoginPage'));
const PatientRegistrationPage = lazy(() => import('../pages/patient/PatientRegistrationPage'));
const PatientDashboard = lazy(() => import('../pages/patient/PatientDashboard'));
const PatientProfilePage = lazy(() => import('../pages/patient/PatientProfilePage'));
const MedicalHistoryIntake = lazy(() => import('../pages/patient/MedicalHistoryIntake'));
const DocumentUploadPage = lazy(() => import('../pages/patient/DocumentUploadPage'));
const UpcomingProcedures = lazy(() => import('../pages/patient/UpcomingProcedures'));
const PatientMessages = lazy(() => import('../pages/patient/PatientMessages'));
const PatientProfileSetup = lazy(() => import('../pages/patient/PatientProfileSetup'));
const PatientForgotPassword = lazy(() => import('../pages/patient/PatientForgotPassword'));
const PatientResetPassword = lazy(() => import('../pages/patient/PatientResetPassword'));
const PatientVerifyEmail = lazy(() => import('../pages/patient/PatientVerifyEmail'));
const AppointmentRequestPage = lazy(() => import('../pages/patient/AppointmentRequestPage'));
const MyAppointments = lazy(() => import('../pages/patient/MyAppointments'));
const QuestionnairesListPage = lazy(() => import('../pages/patient/QuestionnairesListPage'));
const QuestionnairePage = lazy(() => import('../pages/patient/QuestionnairePage'));
const PatientTodos = lazy(() => import('../pages/patient/PatientTodos'));

// Patient Protected Route Component
const PatientProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useEnterpriseAuth();
  const [localAuthChecked, setLocalAuthChecked] = useState(false);
  const [localAuthenticated, setLocalAuthenticated] = useState(false);
  
  useEffect(() => {
    // First check if user is authenticated via main auth system
    if (!isLoading && isAuthenticated && user) {
      const isPatientUser = user.userType === 'patient' || user.role === 'patient' || user.role === 'PATIENT';
      if (isPatientUser) {
        setLocalAuthenticated(true);
        setLocalAuthChecked(true);
        return;
      }
    }
    
    // Fallback to legacy patient auth check only if main auth is not loading
    if (!isLoading && !isAuthenticated) {
      const verifyAuth = async () => {
        const { authenticated } = await checkPatientAuth();
        setLocalAuthenticated(authenticated);
        setLocalAuthChecked(true);
      };
      verifyAuth();
    }
  }, [isLoading, isAuthenticated, user]);
  
  // Show loading while checking auth
  if (isLoading || !localAuthChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Check for recent login (grace period)
  const justLoggedIn = sessionStorage.getItem('justLoggedIn');
  const hasGracePeriod = justLoggedIn && (Date.now() - parseInt(justLoggedIn)) < 5000;
  
  // If not authenticated and no grace period, redirect to unified login
  if (!localAuthenticated && !hasGracePeriod) {
    // Redirect to unified, neutral login (no pre-selected mode)
    return <Navigate to={routes.login()} replace />;
  }
  
  return children;
};

// Patient Portal Layout Inner Component
const PatientLayoutInner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const patientData = JSON.parse(localStorage.getItem('patientData') || '{}');
  const { unreadCount } = useMessageUnread();
  
  // TODO: Get pending todos count from API
  const pendingTodos = 0;
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {isMobile ? (
        <MobilePatientHeader patient={patientData} notifications={unreadCount} />
      ) : (
        <PatientHeader patient={patientData} />
      )}
      <Box sx={{ 
        mt: 0, 
        pb: isMobile ? '56px' : 0, // Add padding for bottom navigation
        minHeight: '100vh'
      }}>
        <Outlet />
      </Box>
      {isMobile && (
        <PatientBottomNavigation 
          unreadMessages={unreadCount} 
          pendingTodos={pendingTodos} 
        />
      )}
    </Box>
  );
};

// Patient Portal Layout Component
const PatientLayout = () => {
  return (
    <MessageUnreadProvider>
      <PatientLayoutInner />
    </MessageUnreadProvider>
  );
};

// Export patient routes configuration
export const patientRoutes = (
  <Route path="/patient">
    {/* Public patient routes */}
    {/* Redirect old patient login to unified login with patient mode */}
    <Route path="login" element={<Navigate to={routes.login()} replace />} />
    <Route path="register" element={<PatientRegistrationPage />} />
    <Route path="forgot-password" element={<PatientForgotPassword />} />
    <Route path="reset-password" element={<PatientResetPassword />} />
    <Route path="verify-email" element={<PatientVerifyEmail />} />
    
    {/* Protected patient routes */}
    <Route
      element={
        <PatientProtectedRoute>
          <PatientLayout />
        </PatientProtectedRoute>
      }
    >
      <Route path="dashboard" element={<PatientDashboard />} />
      <Route path="profile" element={<PatientProfilePage />} />
      <Route path="profile/setup" element={<PatientProfileSetup />} />
      <Route path="medical-history" element={<MedicalHistoryIntake />} />
      <Route path="documents" element={<DocumentUploadPage />} />
      <Route path="procedures" element={<UpcomingProcedures />} />
      <Route path="messages" element={<PatientMessages />} />
      <Route path="appointments" element={<MyAppointments />} />
      <Route path="appointments/request" element={<AppointmentRequestPage />} />
      <Route path="appointments/:id" element={<MyAppointments />} />
      <Route path="questionnaires" element={<QuestionnairesListPage />} />
      <Route path="questionnaires/:questionnaireId" element={<QuestionnairePage />} />
      <Route path="questionnaires/:questionnaireId/:responseId" element={<QuestionnairePage />} />
      <Route path="todos" element={<PatientTodos />} />
      
      {/* Default redirect */}
      <Route index element={<Navigate to="dashboard" replace />} />
    </Route>
  </Route>
);

// Usage in App.jsx:
// import { patientRoutes } from './routes/patientRoutes';
// 
// In the Routes component:
// <Routes>
//   {/* Existing routes */}
//   {patientRoutes}
// </Routes>
